import {
  IBoardObserver,
  IBoardSeat,
  IFinancingBoard,
  IFinancingRights,
} from '../data-models/round-governance.data-model';
import { FundDataModel } from '../schemas/Fund.schema';
import { normalizeFundLabel } from './fundMatchKey';

/**
 * Round-governance derivations shared by every surface that reports them.
 *
 * These were private to the Deals tab's `RoundGovernanceSection` until the company detail
 * panel (FD-4481) needed the same answers. They live here rather than being imported from
 * that component for two reasons: importing a page's section would drag its AgTable
 * dependency into an unrelated bundle, and page-to-page imports make a rule owned by
 * whichever surface happened to need it first.
 *
 * The reason they are shared at all rather than reimplemented: the backend does not
 * attribute board seats or investor rights to a fund id, so "does the firm hold a seat"
 * is answered by matching names — the org's fund names, legal names and aliases,
 * lowercased and trimmed — against the firm on each row. That rule is subtle, it is
 * temporary, and two surfaces quietly disagreeing about whether the firm holds a board
 * seat is worse than either being imperfect.
 */

export type YesNoValue = 'Yes' | 'No' | null;

const byDateDescNullsLast = <T extends { date: string | null }>(a: T, b: T): number => {
  if (a.date == null) {
    return b.date == null ? 0 : 1;
  }
  if (b.date == null) {
    return -1;
  }
  return b.date.localeCompare(a.date);
};

/** Latest financing by date (nulls last); both governance endpoints usually return one. */
export function selectPrimaryFinancing<T extends { date: string | null }>(
  financings: T[] | undefined
): T | null {
  if (!financings?.length) {
    return null;
  }
  return [...financings].sort(byDateDescNullsLast)[0];
}

/** Extractions may pack several fund names into one firm string: one per line, or "A, LLC and B, LLC". */
export function splitFirmNames(name: string | null): string[] {
  const lines = (name ?? '')
    .split(/\r?\n/)
    .map((part) => part.trim())
    .filter(Boolean);
  // Whole lines first, so a fund whose own name carries a comma still matches as one.
  const pieces = lines.flatMap((line) =>
    line
      .split(/,\s+|\s+and\s+/i)
      .map((part) => part.trim())
      .filter(Boolean)
  );
  return [...lines, ...pieces];
}

/** Normalized org fund identifiers (name, legalName, aliases) for firm matching. */
export function buildOrgFundNameSet(funds: FundDataModel[]): Set<string> {
  const names = new Set<string>();
  for (const fund of funds) {
    for (const name of [fund.name, fund.legalName, ...(fund.aliases ?? [])]) {
      if (name) {
        names.add(normalizeFundLabel(name));
      }
    }
  }
  return names;
}

/**
 * Every normalized identifier of every org fund, mapped back to its fund. Lets callers
 * that need identity — not just membership — collapse a fund's name, legal name, and
 * aliases to one fund instead of counting each spelling separately.
 */
export function buildOrgFundsByKey(funds: FundDataModel[]): Map<string, FundDataModel> {
  const byKey = new Map<string, FundDataModel>();
  for (const fund of funds) {
    for (const name of [fund.name, fund.legalName, ...(fund.aliases ?? [])]) {
      if (name) {
        const key = normalizeFundLabel(name);
        if (!byKey.has(key)) {
          byKey.set(key, fund);
        }
      }
    }
  }
  return byKey;
}

/**
 * A newline in a firm string is ambiguous: usually a multi-fund separator, sometimes a
 * soft wrap inside one long name. Try the whole string first (whitespace collapsing in
 * the key handles the wrap), then each part.
 */
export function matchesOrgFund(name: string | null, orgFundNames: Set<string>): boolean {
  if (name != null && orgFundNames.has(normalizeFundLabel(name))) {
    return true;
  }
  return splitFirmNames(name).some((part) => orgFundNames.has(normalizeFundLabel(part)));
}

/** OR-combine independent extractions of the same fact: any Yes wins, else any explicit No, else unknown. */
export function combineYesNo(values: YesNoValue[]): YesNoValue {
  if (values.includes('Yes')) {
    return 'Yes';
  }
  if (values.includes('No')) {
    return 'No';
  }
  return null;
}

/**
 * Whether one of the org's funds holds a board seat (FILLED or HELD_UNNAMED).
 * Best-effort name match until the BE wires fundId to client attribution.
 */
export function deriveFirmBoardSeat(board: IFinancingBoard | null, orgFundNames: Set<string>): YesNoValue {
  if (!board || board.seats.length === 0) {
    return null;
  }
  const held = board.seats.some(
    (seat) => seat.seatStatus !== 'VACANT' && matchesOrgFund(seat.firm, orgFundNames)
  );
  return held ? 'Yes' : 'No';
}

/** Tile value from the org's matched entries in investors[]: any true = Yes, all false = No, unknown = dash. */
export function deriveInvestorRightsTile(
  rights: IFinancingRights | null,
  orgFundNames: Set<string>,
  field: 'isMajorInvestor' | 'hasInformationRights' | 'hasProRataRights'
): YesNoValue {
  if (!rights) {
    return null;
  }
  const values = rights.investors
    .filter((investor) => matchesOrgFund(investor.fundName, orgFundNames))
    .map((investor) => investor[field])
    .filter((value): value is boolean => value != null);
  if (values.some(Boolean)) {
    return 'Yes';
  }
  if (values.length > 0) {
    return 'No';
  }
  return null;
}

/**
 * A round can carry one financing per parsed deal doc, each an independent — and
 * usually partial — extraction of the same event. Position facts must consider all
 * of them: whichever doc names the firm's seat or rights is the one that knows.
 * These OR the per-financing answers directly — never derive a position fact from
 * a merged board, whose per-row firm attribution has to pick one variant.
 */
export function deriveFirmBoardSeatAcross(
  financings: IFinancingBoard[] | undefined,
  orgFundNames: Set<string>
): YesNoValue {
  return combineYesNo((financings ?? []).map((board) => deriveFirmBoardSeat(board, orgFundNames)));
}

export function deriveInvestorRightsTileAcross(
  financings: IFinancingRights[] | undefined,
  orgFundNames: Set<string>,
  field: 'isMajorInvestor' | 'hasInformationRights' | 'hasProRataRights'
): YesNoValue {
  return combineYesNo(
    (financings ?? []).map((rights) => deriveInvestorRightsTile(rights, orgFundNames, field))
  );
}

/**
 * Information Rights tile: matched investor entries win, checked across every financing.
 * The fallback reads only the primary financing's informationRightsForAll — that field is
 * a term, and the Deal Terms chip beside the tile shows the primary's value; the two must
 * not disagree about the same cell of data.
 */
export function deriveInformationRightsTile(
  financings: IFinancingRights[] | undefined,
  orgFundNames: Set<string>
): YesNoValue {
  const matched = deriveInvestorRightsTileAcross(financings, orgFundNames, 'hasInformationRights');
  if (matched != null) {
    return matched;
  }
  return selectPrimaryFinancing(financings)?.terms.informationRightsForAll === true ? 'Yes' : null;
}

/** Whether replacing the kept row's firm attribution with the candidate's gains information. */
function betterFirm(known: string | null, candidate: string | null, orgFundNames: Set<string>): boolean {
  if (!candidate) {
    return false;
  }
  if (!known) {
    return true;
  }
  return !matchesOrgFund(known, orgFundNames) && matchesOrgFund(candidate, orgFundNames);
}

/**
 * One board for the display grid from all of the round's financings — presentation
 * only; position tiles use the *Across derivations instead. Named rows dedupe by
 * member name, walking financings latest-first so the newest doc's attribution wins;
 * within a name, a firm the org recognizes beats one it does not, and any firm beats
 * none. Unnamed held rows dedupe by fund — any recognized spelling of the same org
 * fund is one key — at each financing's maximum (the same seat re-extracted from N
 * docs is still one seat) and are suppressed where another doc names that firm's
 * holder. Only financings sharing the primary's source take part; vacant rows and
 * metadata come from the primary financing.
 */
export function mergeFinancingBoards(
  financings: IFinancingBoard[] | undefined,
  funds: FundDataModel[] = []
): IFinancingBoard | null {
  const primary = selectPrimaryFinancing(financings);
  if (!primary) {
    return null;
  }
  // The BE serves one source per round; if that ever breaks, merge only primary's kind so a
  // seat never inherits a source it did not come from.
  const sameSource = financings!.filter((financing) => financing.source === primary.source);
  if (sameSource.length === 1) {
    return primary;
  }

  const orgFundNames = buildOrgFundNameSet(funds);
  const fundsByKey = buildOrgFundsByKey(funds);
  // Dedupe key for a firm string: the FUND when the org recognizes any spelling in it
  // (alias in one doc, legal name in another — still one fund), else the normalized
  // string, so unrecognized firms with the same spelling still collapse.
  const firmKey = (firm: string | null): string => {
    for (const candidate of firm != null ? [firm, ...splitFirmNames(firm)] : []) {
      const fund = fundsByKey.get(normalizeFundLabel(candidate));
      if (fund) {
        return `fund:${fund.id}`;
      }
    }
    return normalizeFundLabel(firm ?? '');
  };

  const ordered = [...sameSource].sort(byDateDescNullsLast);
  const namedSeats = new Map<string, IBoardSeat>();
  const unnamedHeldByFirm = new Map<string, { seat: IBoardSeat; max: number }>();
  const namedObservers = new Map<string, IBoardObserver>();
  const unnamedObserversByFirm = new Map<string, { observer: IBoardObserver; max: number }>();

  for (const board of ordered) {
    const unnamedHeldCounts = new Map<string, number>();
    const unnamedObserverCounts = new Map<string, number>();

    for (const seat of board.seats) {
      if (seat.seatStatus === 'VACANT') {
        continue;
      }
      if (seat.memberName) {
        const key = normalizeFundLabel(seat.memberName);
        const known = namedSeats.get(key);
        if (!known || betterFirm(known.firm, seat.firm, orgFundNames)) {
          namedSeats.set(key, seat);
        }
      } else {
        const key = firmKey(seat.firm);
        unnamedHeldCounts.set(key, (unnamedHeldCounts.get(key) ?? 0) + 1);
        if (!unnamedHeldByFirm.has(key)) {
          unnamedHeldByFirm.set(key, { seat, max: 0 });
        }
      }
    }
    for (const observer of board.observers) {
      if (observer.name) {
        const key = normalizeFundLabel(observer.name);
        const known = namedObservers.get(key);
        if (!known || betterFirm(known.firm, observer.firm, orgFundNames)) {
          namedObservers.set(key, observer);
        }
      } else {
        const key = firmKey(observer.firm);
        unnamedObserverCounts.set(key, (unnamedObserverCounts.get(key) ?? 0) + 1);
        if (!unnamedObserversByFirm.has(key)) {
          unnamedObserversByFirm.set(key, { observer, max: 0 });
        }
      }
    }

    for (const [key, count] of unnamedHeldCounts) {
      const entry = unnamedHeldByFirm.get(key)!;
      entry.max = Math.max(entry.max, count);
    }
    for (const [key, count] of unnamedObserverCounts) {
      const entry = unnamedObserversByFirm.get(key)!;
      entry.max = Math.max(entry.max, count);
    }
  }

  // A doc that names the seat holder and one that only counts the firm's seats are
  // describing the same seat — the named row absorbs the unnamed one.
  const namedFirmCounts = new Map<string, number>();
  for (const seat of namedSeats.values()) {
    const key = firmKey(seat.firm);
    namedFirmCounts.set(key, (namedFirmCounts.get(key) ?? 0) + 1);
  }
  const namedObserverFirmCounts = new Map<string, number>();
  for (const observer of namedObservers.values()) {
    const key = firmKey(observer.firm);
    namedObserverFirmCounts.set(key, (namedObserverFirmCounts.get(key) ?? 0) + 1);
  }

  const seats: IBoardSeat[] = [...namedSeats.values()];
  for (const [key, { seat, max }] of unnamedHeldByFirm) {
    const remaining = Math.max(0, max - (key ? (namedFirmCounts.get(key) ?? 0) : 0));
    for (let n = 0; n < remaining; n++) {
      seats.push(seat);
    }
  }
  for (const seat of primary.seats) {
    if (seat.seatStatus === 'VACANT') {
      seats.push(seat);
    }
  }
  const observers: IBoardObserver[] = [...namedObservers.values()];
  for (const [key, { observer, max }] of unnamedObserversByFirm) {
    const remaining = Math.max(0, max - (key ? (namedObserverFirmCounts.get(key) ?? 0) : 0));
    for (let n = 0; n < remaining; n++) {
      observers.push(observer);
    }
  }

  return { ...primary, seats, observers };
}
